/**
 * Dynamic tool registry for Radar.
 *
 * New tools register themselves here instead of modifying page.tsx.
 * The dashboard reads from this registry to build its UI and orchestration.
 */

// ── Tool Definition Interface ───────────────────────────────────────────────

export interface ToolDefinition {
  /** Unique key used for state management, URL params, and keyboard shortcuts */
  key: string
  /** Display name shown in the dashboard */
  name: string
  /** Brand hex color for the tool's visual indicators */
  color: string
  /** API endpoint path (POST) */
  endpoint: string
  /** Whether this tool requires brand name input */
  needsBrand: boolean
  /** Estimated completion time in seconds (shown in progress UI) */
  estSeconds: number
  /** Tool category for grouping in the dashboard */
  category: 'technical' | 'citation' | 'intelligence' | 'competitive'
  /** Access tier: 1 = free, 2 = token required, 3 = premium */
  tier: 1 | 2 | 3
  /** Build the request body for this tool's API call */
  bodyBuilder: (params: AuditParams) => Record<string, string>
  /** Short description shown in tool tooltips */
  description?: string
}

export interface AuditParams {
  url: string
  brand: string
  category: string
  keywords?: string
  locale?: string
  question?: string
  pageUrl?: string
}

// ── Registry ────────────────────────────────────────────────────────────────

const registry: ToolDefinition[] = []

export function registerTool(tool: ToolDefinition): void {
  const existing = registry.findIndex(t => t.key === tool.key)
  if (existing >= 0) {
    registry[existing] = tool
  } else {
    registry.push(tool)
  }
}

export function getTools(): readonly ToolDefinition[] {
  return registry
}

export function getTool(key: string): ToolDefinition | undefined {
  return registry.find(t => t.key === key)
}

export function getToolsByCategory(
  category: ToolDefinition['category']
): ToolDefinition[] {
  return registry.filter(t => t.category === category)
}

// ── Register Core Tools ─────────────────────────────────────────────────────

registerTool({
  key: 'crawl',
  name: 'Crawl Check',
  color: '#008dcc',
  endpoint: '/api/platform/tools/crawl-check',
  needsBrand: false,
  estSeconds: 15,
  category: 'technical',
  tier: 1,
  description:
    'Tests 17 AI, search, and SEO crawler user-agents for access to your site',
  bodyBuilder: p => ({ url: p.url }),
})

registerTool({
  key: 'robots',
  name: 'Robots.txt',
  color: '#8b5cf6',
  endpoint: '/api/platform/tools/robots-analyzer',
  needsBrand: false,
  estSeconds: 10,
  category: 'technical',
  tier: 1,
  description: 'Deep parse of robots.txt rules for 16 bots',
  bodyBuilder: p => ({ url: p.url }),
})

registerTool({
  key: 'llms',
  name: 'llms.txt',
  color: '#22c55e',
  endpoint: '/api/platform/tools/llms-validator',
  needsBrand: false,
  estSeconds: 10,
  category: 'technical',
  tier: 1,
  description: 'Validates llms.txt structure, sections, and entities',
  bodyBuilder: p => ({ url: p.url }),
})

registerTool({
  key: 'readiness',
  name: 'AI Readiness',
  color: '#FDC304',
  endpoint: '/api/platform/tools/ai-readiness',
  needsBrand: false,
  estSeconds: 20,
  category: 'technical',
  tier: 1,
  description: '5-category audit across all AI dimensions',
  bodyBuilder: p => ({ url: p.url }),
})

registerTool({
  key: 'citation',
  name: 'Citations',
  color: '#F90B8A',
  endpoint: '/api/platform/tools/citation-check',
  needsBrand: true,
  estSeconds: 30,
  category: 'citation',
  tier: 2,
  description: 'Tracks brand citations across 4 AI providers',
  bodyBuilder: p => ({ url: p.url, brand: p.brand, category: p.category }),
})

registerTool({
  key: 'reddit',
  name: 'Reddit',
  color: '#f97316',
  endpoint: '/api/platform/tools/reddit-monitor',
  needsBrand: true,
  estSeconds: 25,
  category: 'intelligence',
  tier: 2,
  description: 'Discovers brand mentions and detects seeded content',
  // The audited URL is the first-party evidence entity verification needs to
  // tell a real brand mention from a same-name coincidence. The dashboard has
  // it, so it must be sent, not dropped.
  bodyBuilder: p => {
    const body: Record<string, string> = { brand: p.brand, url: p.url }
    if (p.keywords) body.keywords = p.keywords
    return body
  },
})

registerTool({
  key: 'youtube',
  name: 'YouTube',
  color: '#FF0000',
  endpoint: '/api/tools/youtube-monitor',
  needsBrand: true,
  estSeconds: 18,
  category: 'intelligence',
  tier: 2,
  description: 'Tracks brand mentions across YouTube videos and channels',
  bodyBuilder: p => {
    const body: Record<string, string> = { brand: p.brand }
    if (p.keywords) body.keywords = p.keywords
    return body
  },
})

registerTool({
  key: 'aeo',
  name: 'AEO Audit',
  color: '#7c3aed',
  endpoint: '/api/platform/tools/aeo-auditor',
  needsBrand: false,
  estSeconds: 15,
  category: 'technical',
  tier: 1,
  description: 'Scores individual pages for answer engine readiness',
  bodyBuilder: p => ({ url: p.url }),
})

registerTool({
  key: 'promptSov',
  name: 'Prompt SOV',
  color: '#a855f7',
  endpoint: '/api/platform/tools/prompt-sov',
  needsBrand: true,
  estSeconds: 45,
  category: 'competitive',
  tier: 2,
  description: 'Share of voice in AI recommendations for your category',
  bodyBuilder: p => ({ url: p.url, brand: p.brand, category: p.category }),
})

registerTool({
  key: 'sourceInfluence',
  name: 'Source Influence',
  color: '#06b6d4',
  endpoint: '/api/platform/tools/source-influence',
  needsBrand: true,
  estSeconds: 40,
  category: 'intelligence',
  tier: 2,
  description: 'Maps top domains shaping AI narratives in your category',
  bodyBuilder: p => ({ url: p.url, brand: p.brand, category: p.category }),
})

registerTool({
  key: 'hallucinationCheck',
  name: 'Hallucination Check',
  color: '#ef4444',
  endpoint: '/api/platform/tools/hallucination-check',
  needsBrand: true,
  estSeconds: 40,
  category: 'intelligence',
  tier: 2,
  description: 'Detects factual inaccuracies in AI claims about your brand',
  bodyBuilder: p => ({ url: p.url, brand: p.brand }),
})

registerTool({
  key: 'schemaAudit',
  name: 'Schema Audit',
  color: '#14b8a6',
  endpoint: '/api/platform/tools/schema-audit',
  needsBrand: false,
  estSeconds: 15,
  category: 'technical',
  tier: 1,
  description: 'Validates JSON-LD structured data across schema types',
  bodyBuilder: p => ({ url: p.url }),
})

registerTool({
  key: 'answer',
  name: 'Citation Test',
  color: '#dc2626',
  endpoint: '/api/platform/tools/answer-engine',
  needsBrand: true,
  estSeconds: 45,
  category: 'citation',
  tier: 2,
  description: 'Tests if AI engines cite specific URLs for a question',
  bodyBuilder: p => ({
    url: p.url,
    brand: p.brand,
    question:
      p.question || `What is ${p.brand} and what do they specialize in?`,
  }),
})
